import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbdLogoutModal } from '../logout/logout.modal.component';
import { NgbdMessageModal } from '../modal/message.modal.component';
import { SessionService } from '../session/session.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbdSwitchLanguageModal } from '../modal/switch-language.modal.component';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { Language } from '../interfaces/language.interface';
import { RequestCount } from '../interfaces/requestCount.interface';
import { NavMenuService } from './nav-menu.service';
import { SharedStateProvider } from '../sharedStateProvider/shared-state-provider';
import { PortalMode } from '../enum/portalmode.enum';

@Component({
    selector: 'app-nav-menu',
    templateUrl: './nav-menu.component.html',
    styleUrls: ['./nav-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavMenuComponent implements OnInit, OnDestroy {
  protected loggedInSubscription: Subscription;
  protected routerSubscription: Subscription;
  public isLoggedIn: boolean = false;
  public isMobile: boolean;
  public isCollapsed: boolean = true;
  public hasRequests: boolean = false;
  public waitingRequestsCount: number = 0;
  public get selectedCaseId(): string | null { return this.sessionService.getSelectedCaseId() || null; }

  get isBorgenarMode(): boolean {
    return this.sessionService.getPortalMode() === PortalMode.Borgenar;
  }

  get displayBrandName(): string {
    return this.isBorgenarMode ? this.creditorBrandName : this.brandName;
  }

  constructor(
    protected authService: AuthService,
    protected sessionService: SessionService,
    protected translate: TranslateService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected modalService: NgbModal,
    protected title: Title,
    protected navMenuService: NavMenuService,
    @Inject('LANGUAGES') protected languages: Language[],
    @Inject('BRAND_NAME') public brandName: string,
    @Inject('CREDITOR_BRAND_NAME') public creditorBrandName: string,
    private sharedStateProvider: SharedStateProvider) {

    this.sharedStateProvider.waitingRequestsNr.subscribe(nr => { this.waitingRequestsCount = nr })

    this.loggedInSubscription = this.authService.getIsLoggedInMessage().subscribe(
      (newIsLoggedIn: number) => {
        if (!this.isLoggedIn && (newIsLoggedIn === 1)) {
          console.info('Loggade in!');
          this.navMenuService.getNrOfWaitingRequests().subscribe((requestCount: RequestCount) => {
            this.sharedStateProvider.setWaitingRequestNr(requestCount.nrOfWaitingRequests);
            this.hasRequests = requestCount.hasRequests;
          });
        } else if (this.isLoggedIn) {
          if (newIsLoggedIn === 0) { // användaren valde att logga ut genom att trycka på knappen
            console.info('Loggade ut! (frivilligt)');
            this.sessionService.logout();
            if (location.pathname.includes('borgenar'))
              this.router.navigate(['/borgenar']);
            else
              this.router.navigate(['/home']);
          } else if (newIsLoggedIn === 2) { // användaren blev utloggad av annan anledning
            console.info('Blev utloggad!');
            this.sessionService.logout();
            this.modalService.dismissAll();
            let modalRef = this.modalService.open(NgbdMessageModal, { backdrop: 'static', keyboard: false, centered: true });
            modalRef.componentInstance.title = 'Automatisk utloggning';
            modalRef.componentInstance.message = 'Du har blivit automatiskt utloggad.';
            modalRef.componentInstance.icon = 'sign-out-alt';
            modalRef.componentInstance.callback =
              () => {
                if (location.pathname.includes('borgenar'))
                  this.router.navigate(['/borgenar']);
                else
                  this.router.navigate(['/login']);
                return;
              };
          }
          else if (newIsLoggedIn === 3) { // user was logged out due to clicking on code-login-link
            this.modalService.dismissAll();
            this.sessionService.logout();
          }
        }

        this.isLoggedIn = (newIsLoggedIn === 1);
      });
  }

  public ngOnDestroy() {
    this.loggedInSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  public isExpanded: boolean = false;

  public get role() { return this.sessionService.activeInstance.roleType; }
  public get isMinaFordringarActive(): boolean {
    const url = this.router.url.split('?')[0];
    return url.startsWith('/borgenar') && !url.includes('/publicerade-filer');
  }
  public get isOnGroupAcceptPage(): boolean {
    return this.router.url.startsWith('/borgenar/group/accept');
  }
  public get showPubliceradeFiler(): boolean {
    return this.isCreditorMode && !!this.sessionService.getSelectedCaseId() && this.sessionService.getHasCreditorClaims();
  }
  public get name() { return this.sessionService.activeInstance.name; }
  public get isCreditorMode(): boolean { return this.sessionService.getPortalMode() === PortalMode.Borgenar; }
  public get showChangeLogin(): boolean { return !this.isCreditorMode && this.sessionService.numberOfInstances > 1; }
  public get animationState() { return this.isExpanded ? 'in' : this.isMobile ? 'out' : 'off'; }

  public get language() {
    const id = this.translate.getCurrentLang() || this.translate.getFallbackLang();
    const language = this.languages.find(l => l.id === id);
    return language ? language : this.languages[0];
  }

  public collapse() {
    this.isExpanded = false;
  }

  public logout() {
    this.collapse();
    this.modalService.open(NgbdLogoutModal, { centered: true });
  }

  public switchLanguage() {
    this.collapse();
    this.modalService.open(NgbdSwitchLanguageModal, { centered: true });
  }

  public toggle() {
    this.isExpanded = !this.isExpanded;
  }

  public ngOnInit() {
    this.updateTitle();
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd)
        this.updateTitle();
    });
  }

  private updateTitle() {
    this.title.setTitle(this.isBorgenarMode ? this.creditorBrandName : this.brandName);
  }
}
